/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Launch step 1 - every GPU type in one comparison table (the old flow hid
// sold-out GPUs behind "Show all GPUs"). Sold-out rows offer the waitlist.

import React, { useMemo, useState } from 'react';
import { Chip, Skeleton, Tag } from '../../console/ui';
import { SearchIc } from '../../console/icons';
import { GPUAvailability, GPUPricing } from '../../../types';
import { TRAINING_PORTAL_URL } from '../../../config/consoleNav';
import { freeGpus, isInStock } from './catalog';
import { usePriceUnit } from '../../../hooks/usePriceUnit';

interface GpuStepProps {
  gpus: GPUPricing[];
  loading: boolean;
  getAvailability: (gpuType: string) => GPUAvailability | undefined;
  foldSellPrice: (base: number) => number;
  selectedType?: string;
  onSelect: (gpu: GPUPricing) => void;
  onNotify: (gpuType: string) => void;
  notifyingType?: string | null;
}

const GpuStep: React.FC<GpuStepProps> = ({
  gpus,
  loading,
  getAvailability,
  foldSellPrice,
  selectedType,
  onSelect,
  onNotify,
  notifyingType,
}) => {
  const { perMinute } = usePriceUnit();
  const [inStockOnly, setInStockOnly] = useState(false);
  const [bigVram, setBigVram] = useState(false);
  const [query, setQuery] = useState('');

  const sorted = useMemo(
    () => [...gpus].sort((a, b) => (a.price_per_minute || 0) - (b.price_per_minute || 0)),
    [gpus],
  );
  const inStockCount = useMemo(
    () => gpus.filter((g) => isInStock(getAvailability(g.gpu_type))).length,
    [gpus, getAvailability],
  );

  const rows = sorted.filter((g) => {
    if (inStockOnly && !isInStock(getAvailability(g.gpu_type))) return false;
    if (bigVram && (g.memory_gb || 0) < 48) return false;
    const q = query.trim().toLowerCase();
    if (q && !g.gpu_type.toLowerCase().includes(q) && !(g.description || '').toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2 items-center">
        <Chip on={!inStockOnly} onClick={() => setInStockOnly(false)}>
          All GPUs <span className="text-ps-subtle">{gpus.length}</span>
        </Chip>
        <Chip on={inStockOnly} onClick={() => setInStockOnly(true)}>
          In stock now <span className="text-ps-subtle">{inStockCount}</span>
        </Chip>
        <span className="hidden sm:block w-px h-6 bg-ps-strong mx-1.5" aria-hidden="true" />
        <Chip on={bigVram} onClick={() => setBigVram((v) => !v)}>
          ≥ 48 GB VRAM
        </Chip>
        <label className="relative flex-1 min-w-[180px] sm:max-w-[240px]">
          <span className="sr-only">Search GPUs</span>
          <SearchIc className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ps-subtle" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search A100, RTX, H100…"
            className="ps-input pl-9 min-h-[36px]"
          />
        </label>
        <span className="ml-auto text-[13px] text-ps-subtle">Sorted by price, low to high</span>
      </div>

      <div className="ps-card overflow-x-auto">
        <table className="ps-tbl">
          <thead>
            <tr>
              <th>GPU</th>
              <th>VRAM</th>
              <th>Includes</th>
              <th>Availability</th>
              <th className="!text-right">Price</th>
              <th>
                <span className="sr-only">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j}>
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="!py-10 text-center text-ps-muted !whitespace-normal">
                  No GPUs match these filters.{' '}
                  {(inStockOnly || bigVram || query) && (
                    <button
                      type="button"
                      className="ps-link"
                      onClick={() => {
                        setInStockOnly(false);
                        setBigVram(false);
                        setQuery('');
                      }}
                    >
                      Show all GPUs
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((gpu) => {
                const availability = getAvailability(gpu.gpu_type);
                const free = freeGpus(availability);
                const inStock = isInStock(availability);
                const selected = selectedType === gpu.gpu_type;
                const hourly = foldSellPrice(gpu.price_per_minute) * 60;
                return (
                  <tr key={gpu.gpu_type} className={selected ? '[&>td]:!bg-ps-accent-soft' : undefined}>
                    <td>
                      <div className="font-semibold text-ps-text">{gpu.gpu_type}</div>
                      {gpu.description && (
                        <div className="text-xs text-ps-subtle max-w-[260px] truncate" title={gpu.description}>
                          {gpu.description}
                        </div>
                      )}
                    </td>
                    <td>{gpu.memory_gb} GB</td>
                    <td className="text-ps-muted">
                      {gpu.included_cpu_cores || 4} vCPU · {gpu.included_memory_gb || 16} GB
                    </td>
                    <td>
                      {inStock ? (
                        <Tag tone="ok">
                          {free} of {availability?.total_gpus ?? free} free
                        </Tag>
                      ) : availability ? (
                        <Tag tone="off">Sold out · {availability.total_gpus} total</Tag>
                      ) : (
                        <Tag tone="off">Unavailable</Tag>
                      )}
                    </td>
                    <td className="text-right">
                      {perMinute ? (
                        <>
                          <div className="font-semibold text-ps-text">₹{foldSellPrice(gpu.price_per_minute).toFixed(2)}/min</div>
                          <div className="text-xs text-ps-subtle">₹{hourly.toFixed(0)}/hr</div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-ps-text">₹{hourly.toFixed(0)}/hr</div>
                          <div className="text-xs text-ps-subtle">₹{foldSellPrice(gpu.price_per_minute).toFixed(2)}/min</div>
                        </>
                      )}
                    </td>
                    <td className="text-right">
                      {selected ? (
                        <button type="button" className="ps-btn ps-btn-sm min-w-[96px]" aria-pressed="true">
                          Selected
                        </button>
                      ) : inStock ? (
                        <button type="button" className="ps-btn2 ps-btn-sm min-w-[96px]" onClick={() => onSelect(gpu)}>
                          Select
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ps-btn2 ps-btn-sm min-w-[96px]"
                          onClick={() => onNotify(gpu.gpu_type)}
                          disabled={notifyingType === gpu.gpu_type}
                          title="Join the waitlist - we email you when it's back in stock"
                        >
                          {notifyingType === gpu.gpu_type ? 'Joining…' : 'Notify me'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="m-0 text-xs text-ps-subtle">GPU price includes taxes &amp; fees. Extra vCPU and RAM above the included amount are billed separately.</p>

      <div className="ps-card p-[18px] flex flex-wrap gap-4 items-center bg-ps-info-bg border-[#1F3350]">
        <div className="flex-[1_1_360px] min-w-0">
          <div className="font-semibold text-ps-text">Need an H100, H200 or B200 for training?</div>
          <div className="text-sm text-[#B4C6DD] mt-1">
            TrainPods has dedicated training GPUs, billed from the same wallet and managed from the CLI.
          </div>
        </div>
        <a className="ps-btn2" href={`${TRAINING_PORTAL_URL}/console/gpus`} target="_blank" rel="noopener noreferrer">
          See Training GPUs →
        </a>
      </div>
    </div>
  );
};

export default GpuStep;
